import React, { useEffect, useState } from "react";
import { getCategoryUsage } from "@/lib/apiClient";
import { AlertTriangle, Loader2, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DeleteCategoryDialog = ({ cat, categories, onConfirm, onClose }) => {
  const [usage, setUsage] = useState(null);
  const [replacement, setReplacement] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!cat) return;
    setUsage(null);
    setReplacement("");
    getCategoryUsage(cat.code).then(setUsage);
  }, [cat]);

  const inUse = usage && usage.line_items > 0;
  const options = categories.filter((c) => c.code !== cat?.code);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(cat.code, replacement || null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!cat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="delete-category-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-red-500" /> Delete GL {cat?.code}?
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{cat?.name}</span> will be removed from the chart of accounts the AI
            codes against.
          </DialogDescription>
        </DialogHeader>

        {!usage ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking where this code is used…
          </div>
        ) : inUse ? (
          <div className="space-y-4">
            <div
              data-testid="usage-warning"
              className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm"
            >
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <span>
                <span className="font-medium text-amber-500">In use.</span>{" "}
                <span className="font-mono">{usage.line_items}</span> line item{usage.line_items === 1 ? "" : "s"} on{" "}
                <span className="font-mono">{usage.invoices}</span> invoice{usage.invoices === 1 ? "" : "s"} currently use this
                code.
              </span>
            </div>
            <div>
              <label className="text-sm font-medium">Remap those line items to</label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Optional. Leave empty to keep the old code on existing invoices; choose a code to recode them now (each change
                is written to the audit trail).
              </p>
              <Select value={replacement} onValueChange={setReplacement}>
                <SelectTrigger data-testid="replacement-select" className="font-mono text-xs">
                  <SelectValue placeholder="No remap — keep existing coding" />
                </SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {options.map((c) => (
                    <SelectItem key={c.code} value={c.code} className="font-mono text-xs">
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="usage-clear">
            No invoices use this code — safe to delete.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-delete-btn"
            variant="destructive"
            onClick={confirm}
            disabled={busy || !usage}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {inUse && replacement ? (
              <span className="flex items-center gap-1">
                Delete & remap <ArrowRight className="h-3.5 w-3.5" /> {replacement}
              </span>
            ) : inUse ? (
              "Delete, keep existing coding"
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
